import { useNodeId } from "@xyflow/react";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { Button } from "@zapier/design-system";
import { Icon } from "@zapier/zinnia-icons";
import {
  createNewTableAtom_SUSPENDS,
  shouldCreateNewTableAtom,
  updateNodeSetter,
  tablesUsageAtom_SUSPENDS,
  tablesAtom_SUSPENDS,
  saveCanvasSetter,
  closeSecondaryToolbarMenuSetter,
} from "@zapier/canvas-state";
import { Suspense, useCallback, useEffect, useState, useRef } from "react";
import { useEmitResourceCreatedEvents } from "../../hooks/useEmitResourceCreatedEvents";
import { CREATE_NEW_TABLE_BUTTON_TEXT } from "@zapier/canvas-constants";

import { CreateAssetLoadingButton } from "../CreateAssetLoadingButton";
import { ErrorBoundary } from "../ErrorBoundary";
import { UpsellPopover } from "../UpsellPopover";

function SuspendedCreateNewTableButton() {
  const nodeId = useNodeId();
  const setShouldCreateNewTable = useSetAtom(shouldCreateNewTableAtom);
  const createNewTableResponse = useAtomValue(createNewTableAtom_SUSPENDS);
  const updateNode = useSetAtom(updateNodeSetter);
  const saveToBackend = useSetAtom(saveCanvasSetter);
  const [tablesUsage, refreshTablesUsage] = useAtom(tablesUsageAtom_SUSPENDS);
  const refreshTables = useSetAtom(tablesAtom_SUSPENDS);
  const { emitEventOnTableCreation } = useEmitResourceCreatedEvents();
  const closeSecondaryMenu = useSetAtom(closeSecondaryToolbarMenuSetter);

  // Popover state
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const createNewTable = useCallback(() => {
    // If upgrade is needed, show popover instead of creating table
    if (tablesUsage?.needsToUpgrade) {
      setIsPopoverOpen(true);
      return;
    }

    setShouldCreateNewTable(true);
  }, [setShouldCreateNewTable, tablesUsage?.needsToUpgrade]);

  // Reset the shouldCreateNewTableAtom to false after the createNewTableResponse completes
  useEffect(() => {
    if (createNewTableResponse !== null) {
      const tableId = createNewTableResponse.id;

      if (tableId) {
        closeSecondaryMenu();

        const updatedNodes = updateNode({
          id: nodeId!,
          data: {
            tableId,
          },
        });

        saveToBackend({ nodes: updatedNodes });

        refreshTables(); // Trigger tables to be re-fetched, so the new table can be populated in the Tables Typeahead
        refreshTablesUsage(); // Because tableCount would've changed
        setShouldCreateNewTable(false);

        emitEventOnTableCreation(tableId);
      }
    }
  }, [
    refreshTablesUsage,
    createNewTableResponse,
    setShouldCreateNewTable,
    saveToBackend,
    updateNode,
    emitEventOnTableCreation,
    refreshTables,
    nodeId,
    closeSecondaryMenu,
  ]);

  const currentPlanLimit = tablesUsage?.maxTableCount ?? 0;

  return (
    <>
      <Button
        // @ts-ignore
        ref={buttonRef}
        variant={tablesUsage?.needsToUpgrade ? "tertiary" : "primary"}
        size="compact"
        iconPosition="start"
        onPress={createNewTable}
      >
        <Icon name="formAdd" />
        {CREATE_NEW_TABLE_BUTTON_TEXT}
      </Button>
      {tablesUsage?.needsToUpgrade && (
        <UpsellPopover
          assetType="table"
          currentPlanLimit={currentPlanLimit}
          triggerRef={buttonRef}
          isOpen={isPopoverOpen}
          onOpenChange={setIsPopoverOpen}
        />
      )}
    </>
  );
}

export function CreateNewTableButton() {
  return (
    <ErrorBoundary>
      <Suspense
        fallback={
          <CreateAssetLoadingButton>
            {CREATE_NEW_TABLE_BUTTON_TEXT}
          </CreateAssetLoadingButton>
        }
      >
        <SuspendedCreateNewTableButton />
      </Suspense>
    </ErrorBoundary>
  );
}
